import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    ArrowLeft,
    User,
    Phone,
    Calendar,
    Package,
    ShoppingCart,
    Hash,
    Eye,
} from "lucide-react";
import { Tooltip } from "react-tooltip";
import DataTableElement from "../../components/elements/DataTableElement";
import CustomSearchInput from "../../components/form/CustomSearchInput";
import CustomCalendarRange from "../../components/form/CustomCalendarRange";
import { get } from "../../helpers/configRoutes/api_helper";
import { currencyUSD } from "../../helpers/forms/formater";

const formatTimeAgo = (days, months) => {
    if (days === null || days === undefined) return "—";
    if (days === 0) return "Hoy";
    if (days < 30) return `Hace ${days} día${days === 1 ? "" : "s"}`;
    if (months !== null && months < 12) return `Hace ${months} mes${months === 1 ? "" : "es"}`;
    const years = Math.floor((months ?? 0) / 12);
    const restMonths = (months ?? 0) % 12;
    if (restMonths === 0) return `Hace ${years} año${years === 1 ? "" : "s"}`;
    return `Hace ${years} año${years === 1 ? "" : "s"} ${restMonths} mes${restMonths === 1 ? "" : "es"}`;
};

const ClientDetail = () => {
    const navigate = useNavigate();
    const { id } = useParams();

    const [tableLoad, setTableLoad] = useState({ show: true, textLoad: "Cargando ventas del cliente..." });
    const [filters, setFilters] = useState({
        search: "",
        date_start: "",
        date_end: "",
    });
    const [client, setClient] = useState({});
    const [kpis, setKpis] = useState({});
    const [sales, setSales] = useState([]);

    useEffect(() => {
        const getDetail = async () => {
            setTableLoad({ show: true, textLoad: "Cargando ventas del cliente..." });
            const response = await get(`clients/detail/${id}`, { params: filters });
            if (response && response.status) {
                setClient(response.client || {});
                setKpis(response.kpis || {});
                setSales(response.sales || []);
            } else {
                setClient({});
                setKpis({});
                setSales([]);
            }
            setTableLoad({ show: false, textLoad: "" });
        };
        getDetail();
    }, [id, filters]);

    const columns = [
        {
            id: "folio",
            name: "No. FACT",
            selector: (row) => `${row.invoice?.folio ?? "—"}`,
        },
        {
            id: "date",
            name: "Fecha",
            selector: (row) => (
                <span className="flex items-center gap-1 text-sm text-fr-dark">
                    <Calendar size={13} className="text-fr-primary" />
                    {row.date}
                </span>
            ),
        },
        {
            id: "seller",
            name: "Vendedor",
            selector: (row) =>
                row.seller?.information?.name
                    ? `${row.seller?.information?.name} ${row.seller?.information?.last_names ?? ""}`
                    : "—",
        },
        {
            id: "payment_terms",
            name: "Condiciones de pago",
            selector: (row) => `${row.payment_terms ?? "—"}`,
        },
        {
            id: "products",
            name: "Productos",
            selector: (row) => (
                <span className="text-sm text-fr-dark">
                    {row.products?.length ?? 0}
                </span>
            ),
        },
        {
            id: "subtotal",
            name: "Subtotal",
            selector: (row) => (
                <span className="text-fr-success font-semibold bg-fr-success/10 px-3 py-1 rounded-full text-xs whitespace-nowrap">
                    {currencyUSD(row.subtotal ?? 0)}
                </span>
            ),
        },
        {
            id: "total",
            name: "Total",
            selector: (row) => (
                <span className="font-bold text-fr-primary">
                    {currencyUSD(row.total ?? 0)}
                </span>
            ),
        },
        {
            id: "acciones",
            name: "",
            selector: (row) => (
                <button
                    onClick={() => navigate(`/venta/${row.id}`)}
                    className="p-1.5 rounded-lg text-fr-primary hover:bg-fr-primary/10 transition"
                    title="Ver venta"
                    data-tooltip-id={`view-sale-${row.id}`}
                >
                    <Eye size={16} />
                    <Tooltip id={`view-sale-${row.id}`} place="top">
                        Ver detalle de la venta
                    </Tooltip>
                </button>
            ),
        },
    ];

    return (
        <div className="flex flex-col w-full p-5 space-y-5 bg-fr-secondary min-h-screen">
            {/* Encabezado */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate(-1)}
                        className="p-2 rounded-lg bg-fr-light border border-gray-200 text-fr-dark hover:bg-gray-100 transition"
                        title="Regresar"
                    >
                        <ArrowLeft size={16} />
                    </button>
                    <div className="flex flex-col">
                        <h1 className="text-fr-dark text-2xl font-bold tracking-tight">
                            Detalle de Cliente
                        </h1>
                        <p className="text-gray-500 text-xs">
                            Información del cliente y su historial de compras
                        </p>
                    </div>
                </div>
            </div>

            {/* Datos del cliente */}
            <div className="bg-fr-light border border-gray-200 rounded-xl p-5 shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                    <div className="p-2.5 rounded-full bg-fr-primary/10 text-fr-primary">
                        <User size={20} />
                    </div>
                    <div className="flex flex-col">
                        <h2 className="text-fr-dark text-lg font-semibold leading-tight">
                            {client.name ?? "—"} {client.last_names ?? ""}
                        </h2>
                        <p className="text-gray-500 text-xs flex items-center gap-1">
                            <Hash size={11} /> No. {client.no ?? "—"}
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                    <div>
                        <p className="text-xs text-gray-500">Teléfono</p>
                        <p className="text-sm font-semibold text-fr-dark flex items-center gap-1">
                            <Phone size={12} className="text-fr-primary" />
                            {client.phone || "—"}
                        </p>
                    </div>
                    <div>
                        <p className="text-xs text-gray-500">CURP</p>
                        <p className="text-sm font-semibold text-fr-dark">{client.curp || "—"}</p>
                    </div>
                    <div>
                        <p className="text-xs text-gray-500">Fecha de nacimiento</p>
                        <p className="text-sm font-semibold text-fr-dark">{client.birthdate || "—"}</p>
                    </div>
                    <div>
                        <p className="text-xs text-gray-500">Cliente desde</p>
                        <p className="text-sm font-semibold text-fr-dark">
                            {client.created_at ? String(client.created_at).substring(0, 10) : "—"}
                        </p>
                    </div>
                </div>
            </div>

            {/* KPIs del cliente */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                <div className="bg-fr-light border border-gray-200 rounded-xl p-4">
                    <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                        <ShoppingCart size={11} /> Tickets
                    </p>
                    <p className="text-2xl font-bold text-fr-dark">{kpis.total_tickets ?? 0}</p>
                </div>
                <div className="bg-fr-light border border-gray-200 rounded-xl p-4">
                    <p className="text-xs text-gray-500 mb-1">Subtotal histórico</p>
                    <p className="text-xl font-bold text-fr-success">
                        {currencyUSD(kpis.total_subtotal ?? 0)}
                    </p>
                </div>
                <div className="bg-fr-light border border-gray-200 rounded-xl p-4">
                    <p className="text-xs text-gray-500 mb-1">Total histórico</p>
                    <p className="text-xl font-bold text-fr-primary">
                        {currencyUSD(kpis.total_amount ?? 0)}
                    </p>
                </div>
                <div className="bg-fr-light border border-gray-200 rounded-xl p-4">
                    <p className="text-xs text-gray-500 mb-1">Última compra</p>
                    <p className="text-sm font-bold text-fr-dark">
                        {kpis.last_purchase_date || "—"}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">
                        {formatTimeAgo(kpis.days_since_last_purchase, kpis.months_since_last_purchase)}
                    </p>
                </div>
                <div className="bg-fr-light border border-gray-200 rounded-xl p-4">
                    <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                        <Package size={11} /> Producto top
                    </p>
                    {kpis.top_product ? (
                        <>
                            <p className="text-sm font-bold text-fr-dark truncate" title={kpis.top_product.name}>
                                {kpis.top_product.name}
                            </p>
                            <p className="text-xs text-gray-400 mt-0.5">
                                {currencyUSD(kpis.top_product.total_amount ?? 0)}
                            </p>
                        </>
                    ) : (
                        <p className="text-sm text-gray-400">—</p>
                    )}
                </div>
            </div>

            {/* Filtros */}
            <div className="bg-fr-light border border-gray-200 rounded-xl p-4">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full">
                        <div className="min-w-0">
                            <CustomCalendarRange
                                isClearable={true}
                                className="text-sm"
                                holder="Fechas Rango"
                                valueStart={filters.date_start}
                                valueEnd={filters.date_end}
                                filters={filters}
                                setFilters={setFilters}
                            />
                        </div>
                    </div>
                    <div className="w-full lg:w-[280px]">
                        <CustomSearchInput
                            value={filters.search}
                            handleChange={(e) => setFilters({ ...filters, search: e.target.value })}
                            holder="Buscar venta..."
                        />
                    </div>
                </div>
            </div>

            {/* Tabla de ventas */}
            <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                    <ShoppingCart size={16} className="text-fr-primary" />
                    <h3 className="text-fr-dark text-sm font-semibold">
                        Historial de ventas {sales.length > 0 ? `(${sales.length})` : ""}
                    </h3>
                </div>
                <div className="bg-fr-light border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                    <DataTableElement columns={columns} tableLoad={tableLoad} items={sales} />
                </div>
            </div>
        </div>
    );
};

export default ClientDetail;
